import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Alert, Card, CardBody } from 'reactstrap';
import queryString from 'query-string';

import SimpleLoader from '../../components/SimpleLoader';
import { USER_LOGOUT } from '../../store/user/types';
import { setUserAuth, setUserData } from '../../store/user/actions';
import { apm } from '../../index';
import { AppDispatch } from '../../configureStore';

// eslint-disable-next-line @typescript-eslint/no-var-requires
const jwtDecode = require('jwt-decode');

type TokenClaims = {
  email?: string;
  exp?: number;
  name?: string;
  preferred_username?: string;
  role?: number | string;
  scopes?: string[];
};

type EmulationParameters = {
  access_token?: string | string[] | null;
  id_token?: string | string[] | null;
  token?: string | string[] | null;
};

const getParameter = (value: string | string[] | null | undefined): string => (
  Array.isArray(value) ? value[0] || '' : value || ''
);

const getEmulationTokens = (search: string, hash: string) => {
  const queryParams = queryString.parse(search) as EmulationParameters;
  const hashParams = queryString.parse(hash.replace(/^#/, '')) as EmulationParameters;

  return {
    token: getParameter(
      hashParams.access_token
      || hashParams.token
      || queryParams.access_token
      || queryParams.token,
    ),
    idToken: getParameter(hashParams.id_token || queryParams.id_token),
  };
};

type Props = {
  dispatch: AppDispatch;
};

const Emulation: React.FC<Props> = ({ dispatch }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [error, setError] = useState('');

  useEffect(() => {
    const { token, idToken } = getEmulationTokens(location.search, location.hash);

    localStorage.removeItem('hso-api-token');
    localStorage.removeItem('hso-api-refresh-token');
    localStorage.removeItem('hso-api-id-token');
    dispatch({ type: USER_LOGOUT });

    if (!token || !idToken) {
      setError('The emulation link is missing the required access or identity token.');
      return;
    }

    try {
      const accessTokenClaims = jwtDecode(token) as TokenClaims;
      const idTokenClaims = jwtDecode(idToken) as TokenClaims;
      const now = Math.floor(Date.now() / 1000);

      if (!accessTokenClaims.exp || !idTokenClaims.exp) {
        throw new Error('invalid');
      }

      if (accessTokenClaims.exp <= now || idTokenClaims.exp <= now) {
        throw new Error('expired');
      }

      if (!Array.isArray(accessTokenClaims.scopes)
        || !idTokenClaims.email
        || !(idTokenClaims.preferred_username || idTokenClaims.name)) {
        throw new Error('identity');
      }

      localStorage.setItem('hso-api-token', token);
      localStorage.setItem('hso-api-id-token', idToken);
      dispatch(setUserAuth(token, '', false));
      dispatch(setUserData({
        username: idTokenClaims.preferred_username || idTokenClaims.name,
        email: idTokenClaims.email,
        permissions: accessTokenClaims.scopes,
      }));

      apm.setUserContext({
        username: idTokenClaims.preferred_username || idTokenClaims.name,
        email: idTokenClaims.email.toLowerCase(),
      });
      apm.setCustomContext({
        permissions: accessTokenClaims.scopes,
        role: accessTokenClaims.role,
        emulated: true,
      });

      navigate('/dashboard', { replace: true });
    } catch (err) {
      setError(err instanceof Error && err.message === 'expired'
        ? 'This emulation link has expired. Please start a new session from the admin portal.'
        : 'This emulation link is invalid. Please start a new session from the admin portal.');
    }
  }, [dispatch, location.hash, location.search, navigate]);

  return (
    <div className="animated fadeIn">
      <Card>
        <CardBody>
          {error ? (
            <Alert color="danger">
              {error} <Link to="/login">Return to login</Link>
            </Alert>
          ) : (
            <div className="app flex-row align-items-center animated fadeIn">
              <div className="container">
                <SimpleLoader loading>
                  <div className="row flex-column justify-content-center align-items-center" style={{ height: '50%' }} />
                </SimpleLoader>
              </div>
            </div>
          )}
        </CardBody>
      </Card>
    </div>
  );
};

export default connect()(Emulation);
